import { Button, Form, message, Modal, Space } from 'antd';
import type { UserResponse } from '../../../api/types';
import { useT } from '../../../locales';
import {
  useCreateUser,
  useUpdateUser,
  type CreateUserInput,
  type UpdateUserInput,
} from '../api/useUsers';
import { USER_FORM_MODE, UserForm, type UserFormMode, type UserFormValues } from './UserForm';

interface UserFormModalProps {
  open: boolean;
  mode: UserFormMode;
  user?: UserResponse | null;
  onClose: () => void;
}

export function UserFormModal({ open, mode, user, onClose }: UserFormModalProps) {
  const t = useT();
  const [form] = Form.useForm<UserFormValues>();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const isCreate = mode === USER_FORM_MODE.CREATE;
  const isPending = createUser.isPending || updateUser.isPending;

  const closeModal = (force = false) => {
    if (isPending && !force) return;
    form.resetFields();
    onClose();
  };

  const handleCancel = () => closeModal();

  const handleSubmit = async (values: UserFormValues) => {
    try {
      if (isCreate) {
        if (!values.email || !values.username || !values.password) {
          return;
        }

        const input: CreateUserInput = {
          email: values.email,
          username: values.username,
          password: values.password,
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone,
          jobTitle: values.jobTitle,
          department: values.department,
          isActive: values.isActive,
          roleIds: values.roleIds,
        };
        await createUser.mutateAsync(input);
        message.success(t('pages.users.createSuccess'));
      } else {
        if (!user) return;

        const body: UpdateUserInput['body'] = {
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone,
          jobTitle: values.jobTitle,
          department: values.department,
          isActive: values.isActive,
        };
        await updateUser.mutateAsync({ id: user.id, body });
        message.success(t('pages.users.updateSuccess'));
      }

      closeModal(true);
    } catch {
      message.error(t(isCreate ? 'pages.users.createError' : 'pages.users.updateError'));
    }
  };

  return (
    <Modal
      open={open}
      title={t(isCreate ? 'pages.users.create' : 'pages.users.edit')}
      onCancel={handleCancel}
      destroyOnHidden
      afterOpenChange={(nextOpen) => {
        if (!nextOpen) {
          form.resetFields();
        }
      }}
      footer={
        <Space>
          <Button onClick={handleCancel} disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button type="primary" loading={isPending} onClick={() => form.submit()}>
            {t(isCreate ? 'pages.users.submitCreate' : 'pages.users.submitEdit')}
          </Button>
        </Space>
      }
      width={620}
      centered
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
    >
      <UserForm
        form={form}
        mode={mode}
        initialValues={user}
        onSubmit={handleSubmit}
        loading={isPending}
        showActions={false}
      />
    </Modal>
  );
}
